import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  closeSubmittal,
  createSubmittal,
  createSubmittalPackage,
  createSubmittalRevision,
  deleteSubmittal,
  deleteSubmittalAttachment,
  getProjectShareableUsers,
  getSubmittalById,
  getSubmittalPackages,
  getSubmittalRevisions,
  getSubmittalStatusSummary,
  getSubmittals,
  respondToSubmittal,
  submitSubmittal,
  updateSubmittal,
  uploadSubmittalAttachment,
} from '../api/api';
import type { CreateSubmittalData, SubmittalFilters, UpdateSubmittalData } from '../types';

export const submittalKeys = {
  all: ['submittals'] as const,
  lists: (projectId: number) => [...submittalKeys.all, 'list', projectId] as const,
  list: (projectId: number, filters: SubmittalFilters) =>
    [...submittalKeys.lists(projectId), filters] as const,
  detail: (id: string) => [...submittalKeys.all, 'detail', id] as const,
  statusSummary: (projectId: number) => [...submittalKeys.all, 'status-summary', projectId] as const,
  packages: (projectId: number) => [...submittalKeys.all, 'packages', projectId] as const,
  users: (projectId: number) => [...submittalKeys.all, 'users', projectId] as const,
  revisions: (id: string) => [...submittalKeys.all, 'revisions', id] as const,
};

export const useSubmittals = (projectId: number | undefined, filters: SubmittalFilters = {}) =>
  useQuery({
    queryKey: submittalKeys.list(projectId!, filters),
    queryFn: () => getSubmittals(projectId!, filters),
    enabled: !!projectId,
    staleTime: 30_000,
  });

export const useSubmittal = (id: string | undefined, projectId: number | undefined) =>
  useQuery({
    queryKey: submittalKeys.detail(id!),
    queryFn: () => getSubmittalById(id!, projectId!),
    enabled: !!id && !!projectId,
    staleTime: 30_000,
  });

export const useSubmittalStatusSummary = (projectId: number | undefined) =>
  useQuery({
    queryKey: submittalKeys.statusSummary(projectId!),
    queryFn: () => getSubmittalStatusSummary(projectId!),
    enabled: !!projectId,
    staleTime: 30_000,
  });

export const useSubmittalPackages = (projectId: number | undefined) =>
  useQuery({
    queryKey: submittalKeys.packages(projectId!),
    queryFn: () => getSubmittalPackages(projectId!),
    enabled: !!projectId,
    staleTime: 60_000,
  });

export const useProjectUsers = (projectId: number | undefined) =>
  useQuery({
    queryKey: submittalKeys.users(projectId!),
    queryFn: () => getProjectShareableUsers(projectId!),
    enabled: !!projectId,
    staleTime: 5 * 60_000,
  });

export const useSubmittalRevisions = (id: string | undefined) =>
  useQuery({
    queryKey: submittalKeys.revisions(id!),
    queryFn: () => getSubmittalRevisions(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

export const useSubmittalMutations = (projectId: number | undefined) => {
  const queryClient = useQueryClient();

  const invalidateProject = () => {
    if (!projectId) return;
    queryClient.invalidateQueries({ queryKey: submittalKeys.lists(projectId) });
    queryClient.invalidateQueries({ queryKey: submittalKeys.statusSummary(projectId) });
    queryClient.invalidateQueries({ queryKey: submittalKeys.packages(projectId) });
  };

  const refreshDetail = (id: string) => {
    queryClient.invalidateQueries({ queryKey: submittalKeys.detail(id) });
    queryClient.invalidateQueries({ queryKey: submittalKeys.revisions(id) });
    invalidateProject();
  };

  const createItem = useMutation({
    mutationFn: (data: CreateSubmittalData) => createSubmittal(data),
    onSuccess: () => invalidateProject(),
  });

  const updateItem = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSubmittalData }) => updateSubmittal(id, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: submittalKeys.detail(result.id) });
      invalidateProject();
    },
  });

  const deleteItem = useMutation({
    mutationFn: (id: string) => deleteSubmittal(id),
    onSuccess: () => invalidateProject(),
  });

  const submitItem = useMutation({
    mutationFn: (id: string) => submitSubmittal(id),
    onSuccess: (result) => refreshDetail(result.id),
  });

  const respondItem = useMutation({
    mutationFn: ({
      id,
      reviewer_assignment,
      response_code,
      comment,
    }: {
      id: string;
      reviewer_assignment: string;
      response_code: 'APPROVED' | 'APPROVED_WITH_COMMENTS' | 'REVISE_RESUBMIT' | 'REJECTED' | 'APPROVED_AS_NOTED' | 'FOR_RECORD_ONLY' | 'VOID';
      comment?: string;
    }) => respondToSubmittal(id, { reviewer_assignment, response_code, comment }),
    onSuccess: (result) => refreshDetail(result.id),
  });

  const closeItem = useMutation({
    mutationFn: ({
      id,
      final_response_code,
      close_comments,
      distribution_recipient_ids,
    }: {
      id: string;
      final_response_code:
        | 'FOR_RECORD_ONLY'
        | 'APPROVED'
        | 'APPROVED_AS_NOTED'
        | 'APPROVED_WITH_COMMENTS'
        | 'REVISE_RESUBMIT'
        | 'REJECTED'
        | 'VOID';
      close_comments?: string;
      distribution_recipient_ids?: number[];
    }) => closeSubmittal(id, { final_response_code, close_comments, distribution_recipient_ids }),
    onSuccess: (result) => refreshDetail(result.id),
  });

  const createRevision = useMutation({
    mutationFn: ({ id, title, description }: { id: string; title?: string; description?: string }) =>
      createSubmittalRevision(id, { title, description }),
    onSuccess: (result) => refreshDetail(result.id),
  });

  const createPackage = useMutation({
    mutationFn: (payload: {
      project: number;
      title: string;
      description?: string;
      package_number?: string;
      discipline?: string;
    }) => createSubmittalPackage(payload),
    onSuccess: () => invalidateProject(),
  });

  const uploadAttachment = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => uploadSubmittalAttachment(id, file),
    onSuccess: (_data, { id }) => refreshDetail(id),
  });

  const deleteAttachment = useMutation({
    mutationFn: ({ id, attachmentId }: { id: string; attachmentId: string }) =>
      deleteSubmittalAttachment(attachmentId).then(() => id),
    onSuccess: (id) => refreshDetail(id),
  });

  return {
    createItem,
    updateItem,
    deleteItem,
    submitItem,
    respondItem,
    closeItem,
    createRevision,
    createPackage,
    uploadAttachment,
    deleteAttachment,
  };
};
